import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Users, House, Rocket, Plus, Clock } from "lucide-react";
import { useAuthService } from "../auth/sso/hooks/useAuthService";
import { useSAPCDC } from "../auth/sapcdc/hooks/useSAPCDC";
import instanceIcon from "../images/Adapt_Logo.jpg";
import cardIcon from "../images/adapt-logo-instance.webp";
import Header from "./ui/Header";
import Footer from "./ui/Footer";
import RequestInstanceAccess from "./RequestInstanceAccess";
import devInstanceAccessService from "../services/DevInstanceAccessService";
import adaptUserService from "../users/services/AdaptUserService";
import { useAuth } from "../contexts/AuthContext";

const InstanceSwitch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthService();
  const { logout: sapCDCLogout } = useSAPCDC();
  const { authState, getCurrentUser, logout: contextLogout } = useAuth();
  const [instances, setInstances] = useState([]);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [helpUrl, setHelpUrl] = useState('');
  const [hasMoreProdInstances, setHasMoreProdInstances] = useState(true);
  const [canAccessDev, setCanAccessDev] = useState(null);

  // Long-press state for mobile "open in new tab" functionality
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const LONG_PRESS_DURATION = 500; // milliseconds

  // Get user data from AuthContext
  const { user, email, loginType } = getCurrentUser();
  const accessibleInstances = authState.accessibleInstances || [];
  const verificationResult = authState.verificationResult;

  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: ''
  });

  // Helper function to show notification
  const showNotification = (title, message) => {
    setNotification({
      show: true,
      title: title,
      message: message
    });
  };

  // Handle OK button click in notification
  const handleNotificationOK = () => {
    setNotification({ show: false, title: '', message: '' });
  };

  // Custom Notification Dialog Component
  const NotificationDialog = () => {
    if (!notification.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-8">
          <div className="text-center">
            {/* Blue Info Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full border-2 border-blue-500 mb-4">
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            {/* Title */}
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {notification.title}
            </h3>
            
            {/* Message */}
            <p className="text-gray-600 mb-6 leading-relaxed whitespace-pre-line">
              {notification.message}
            </p>
            
            {/* OK Button */}
            <button
              onClick={handleNotificationOK}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-8 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    // Load help URL from config.json
    fetch('/config.json', { cache: 'no-store' })
      .then(response => response.json())
      .then(config => {
        if (config.helpUrl) {
          setHelpUrl(config.helpUrl);
        }
      })
      .catch(error => console.error('Error loading help URL from config:', error));
  }, []);

  useEffect(() => {
    // Resolve DEV-instance access for the current user so the value is
    // available to the Request Access modal even when the user has no
    // accessible instances yet.
    const userEmail = email || user;
    if (!userEmail) return;
    devInstanceAccessService.canAccessDevInstances(userEmail)
      .then(result => setCanAccessDev(result))
      .catch(error => {
        console.error('❌ [Adapt-Instances] Error resolving DEV access:', error);
        setCanAccessDev(false);
      });
  }, [email, user]);

  useEffect(() => {
    // If user has verified access from SSO, use those instances
    if (accessibleInstances.length > 0) {
      const filterAndSetInstances = async () => {
        const userEmail = email || user;
        
        // Check if user has access to DEV instances
        const canAccessDev = await devInstanceAccessService.canAccessDevInstances(userEmail);
        
        // Filter instances based on DEV access
        const filteredInstances = accessibleInstances.filter(instance => {
          const isDevInstance = instance.id.includes('-dev-') ||
                               instance.id.includes('dev-') ||
                               instance.name.toLowerCase().includes('dev') ||
                               instance.id === 'adapt-tryout-test';

          // If it's a DEV instance and user doesn't have access, filter it out
          if (isDevInstance && !canAccessDev) {
            console.log(`🚫 [Adapt-Instances] Filtering out DEV instance: ${instance.name} for user ${userEmail}`);
            return false;
          }

          return true;
        });
        
        const mappedInstances = filteredInstances.map((instance, index) => ({
          id: instance.id,
          name: instance.name,
          role: instance.userRole,
          description: instance.description,
          icon: instanceIcon,
          link: instance.url,
          lastAccess: instance.lastAccess,
          firstAccess: instance.firstAccess,
          isActive: instance.isActive !== false, // Default to true unless explicitly false
        }));
        
        setInstances(mappedInstances);
        
        // Check if user has access to all PROD instances
        // Count PROD instances in accessible instances (excluding DEV)
        const prodInstancesInAccessible = accessibleInstances.filter(instance => {
          const isDevInstance = instance.id.includes('-dev-') ||
                               instance.id.includes('dev-') ||
                               instance.name.toLowerCase().includes('dev') ||
                               instance.id === 'adapt-tryout-test';
          return !isDevInstance;
        });

        // Get total available instances from AdaptUserService
        try {
          const allInstances = adaptUserService.instances;

          if (allInstances && typeof allInstances === 'object') {
            // Count total PROD instances (excluding DEV instances)
            const totalProdInstances = Object.keys(allInstances).filter(instanceKey => {
              const isDevInstance = instanceKey.includes('-dev-') ||
                                   instanceKey.includes('dev-') ||
                                   allInstances[instanceKey].name?.toLowerCase().includes('dev') ||
                                   instanceKey === 'adapt-tryout-test';
              return !isDevInstance;
            });
            
            // If user has access to all PROD instances, hide request section
            const hasAllProdInstances = prodInstancesInAccessible.length >= totalProdInstances.length;
            setHasMoreProdInstances(!hasAllProdInstances);
            
            console.log(`📊 [Adapt-Instances] PROD instances - User has: ${prodInstancesInAccessible.length}, Total available: ${totalProdInstances.length}`);
            console.log(`${hasAllProdInstances ? '✅' : 'ℹ️'} [Adapt-Instances] User has ${hasAllProdInstances ? 'all' : 'partial'} PROD instance access`);
          } else {
            // If instances not available, default to showing request section
            setHasMoreProdInstances(true);
          }
        } catch (error) {
          console.error('❌ [Adapt-Instances] Error checking available instances:', error);
          // On error, default to showing request section
          setHasMoreProdInstances(true);
        }
        
        if (!canAccessDev && filteredInstances.length < accessibleInstances.length) {
          console.log(`ℹ️ [Adapt-Instances] DEV instances hidden for user ${userEmail}`);
        }
      };
      
      filterAndSetInstances();
    } else {
      // Check if user came from SSO login but has no access
      if (verificationResult && !verificationResult.isValid && loginType?.includes('SSO')) {
        // Show notification for SSO users without access
        const userEmail = email || user;
        showNotification(
          'Access Information',
          `Authentication was successful, but no access is available to any Adapt Learning instances for ${userEmail}.\n\nPlease raise request access or contact your administrator.`
        );
      }
    }
  }, [accessibleInstances, email, user, loginType, verificationResult]);

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Direct navigation to instance API endpoint for authentication
  const handleInstanceAccess = async (instance, event) => {
    try {
      console.log(`\n🚀 [Instance Access] Accessing ${instance.name}...`);
      console.log("📋 [Instance Access] Instance details:", {
        id: instance.id,
        name: instance.name,
        url: instance.link,
        role: instance.role,
      });

      // Get current user's email from AuthContext
      const userEmail = email || user;
      
      if (!userEmail || userEmail === "User") {
        console.error("❌ [Instance Access] No valid user email found");
        console.error("❌ Unable to determine user email. Please log in again.");
        return;
      }

      console.log(`🔐 [Instance Access] Redirecting to authentication endpoint for user: ${userEmail}`);

      // Extract the base URL from instance link (remove /#dashboard if present)
      const baseUrl = instance.link.replace('/#dashboard', '');
      
      // Construct the API authentication URL
      const authUrl = `${baseUrl}/api/user/email/${encodeURIComponent(userEmail)}`;
      
      console.log(`🌐 [Instance Access] Redirecting to: ${authUrl}`);

      // Check for modifier keys: Ctrl (Windows/Linux) or Cmd (Mac) to open in new tab
      const openInNewTab = event && (event.ctrlKey || event.metaKey);
      
      if (openInNewTab) {
        console.log("📑 [Instance Access] Opening in new tab (modifier key detected)");
        window.open(authUrl, "_blank");
      } else {
        // Redirect to the authentication endpoint in the same window
        window.location.href = authUrl;
      }

      console.log("✅ [Instance Access] Successfully redirected to authentication endpoint");

    } catch (error) {
      console.error("❌ [Instance Access] Failed to access instance:", error);
      console.error(`❌ Failed to access ${instance.name}. Please try again.`);
    }
  };

  // Long-press handlers for mobile "open in new tab" functionality
  const handleTouchStart = useCallback((instance) => {
    if (!instance.isActive) return;
    
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      // Vibrate for haptic feedback if supported
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      console.log("📱 [Instance Access] Long-press detected, opening in new tab");
      handleInstanceAccess(instance, { ctrlKey: true }); // Simulate Ctrl+Click to open in new tab
    }, LONG_PRESS_DURATION);
  }, []);

  const handleTouchEnd = useCallback((instance, e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    
    // If long-press was triggered, prevent the click event
    if (longPressTriggered.current) {
      e.preventDefault();
      longPressTriggered.current = false;
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    // Cancel long-press if user moves finger
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleLogout = async () => {
    try {
      console.log("\n🚪 [Instance Switch] Starting logout process...");
      console.log("🔍 [Instance Switch] Login type:", loginType);

      if (loginType.includes("Azure AD") || loginType.includes("SSO")) {
        console.log("🔐 [Instance Switch] Performing Azure AD SSO logout...");
        
        // Clear authentication context FIRST to trigger cross-tab logout
        contextLogout();
        console.log("✅ [Instance Switch] Authentication context cleared");
        
        // Use Azure AD logout redirect - it will clear MSAL and redirect
        // Note: This will do a full page redirect, other tabs already logged out via contextLogout
        await logout();
        console.log("✅ [Instance Switch] SSO logout redirect initiated");
        // No navigate needed - logout redirect handles it
      } else if (loginType === "SAP CDC") {
        console.log("🔐 [Instance Switch] Performing SAP CDC logout...");
        
        // Clear authentication context
        contextLogout();
        console.log("✅ [Instance Switch] Authentication context cleared");
        
        // Use SAP CDC logout for SAP CDC users
        await sapCDCLogout();
        console.log("✅ [Instance Switch] SAP CDC logout completed");
        
        // Navigate after SAP CDC logout
        navigate("/");
      }
      
      console.log("✅ Logout successful!");
    } catch (error) {
      console.error("❌ [Instance Switch] Logout error:", error);
      // Ensure context is cleared even on error
      contextLogout();
      console.log("⚠️ Logout completed with warnings. Redirecting to login page...");
      navigate("/");
    }
  };

  return (
    <section className="min-h-screen bg-white flex flex-col justify-between">
      <Header user={user} onLogout={handleLogout} showUserDropdown={true} />
      {/* Custom Notification Dialog */}
      <NotificationDialog />
      <div className="flex flex-1 flex-col items-center bg-white px-4 py-8">
        <div className="w-full max-w-5xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12 border-b-2 border-brand-blue pb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
                <span className="flex items-center justify-center h-10 w-10 bg-primary-100 text-primary-600 rounded-full p-1 mr-2">
                  <House />
                </span>{" "}
                Welcome to Adapt Authoring Tool
              </h1>
              <p className="text-gray-500 mb-2 text-left font-bold">
                You’re all set to begin your course creation journey. 
                We’re here to make it smooth and enjoyable. 
                Whenever you need guidance, feel free to explore our{' '}
                <a 
                  href={helpUrl || '#'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline text-brand-blue hover:text-brand-darkBlue cursor-pointer transition-colors"
                  aria-label="Open Help and How-To documentation in a new tab"
                >
                  Help and How-To
                </a>
                {' '}resources — they're always available to assist you along the way.
              </p>
            </div>
          </div>

          {instances.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-center">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">
                  No Instances Available
                </h3>
                <p className="text-gray-500">
                  You don't have access to any Adapt Learning instances.
                </p>
                <p className="text-gray-500 mt-2">
                  Please raise request access or contact your administrator.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                {instances.map((instance, index) => (
                  <article
                    key={instance.id}
                    className="card shadow-xl border border-gray-100 w-full min-h-[340px] rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 group cursor-pointer"
                    aria-labelledby={`instance-title-${instance.id}`}
                    aria-describedby={`instance-desc-${instance.id}`}
                    onClick={(e) => {
                      // Prevent click if long-press was triggered
                      if (longPressTriggered.current) {
                        e.preventDefault();
                        return;
                      }
                      instance.isActive && handleInstanceAccess(instance, e);
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && instance.isActive) {
                        e.preventDefault();
                        handleInstanceAccess(instance, e);
                      }
                    }}
                    onTouchStart={() => handleTouchStart(instance)}
                    onTouchEnd={(e) => handleTouchEnd(instance, e)}
                    onTouchMove={handleTouchMove}
                    tabIndex={instance.isActive ? 0 : -1}
                    role="button"
                    aria-disabled={!instance.isActive}
                  >
                    {/* Top: Role badge and logo, beige background */}
                    <div
                      className="bg-[#FFF4E8] flex flex-col items-center relative overflow-hidden"
                      style={{ minHeight: "120px" }}
                    >
                      <img
                        src={cardIcon}
                        alt={`${instance.name} logo`}
                        className="object-contain transition-transform duration-300 group-hover:scale-110"
                      />
                      <span
                        className="absolute left-4 top-4 flex items-center gap-1 text-xs font-semibold bg-[#F8E2BF] text-[#806128] px-2 py-1 rounded-md shadow border border-[#F8E2BF]"
                        aria-label={`User role: ${
                          instance.role || "Course Creator"
                        }`}
                      >
                        {instance.role || "Course Creator"}
                      </span>
                    </div>
                    {/* Bottom: Info section, white background */}
                    <div className="flex flex-col justify-between px-6 py-5 bg-white text-left rounded-b-2xl" style={{ minHeight: "180px" }}>
                      <div className="flex-1">
                        <h3
                          id={`instance-title-${instance.id}`}
                          className="text-xl font-bold mb-1 text-gray-900"
                        >
                          {instance.name}
                        </h3>
                        <p
                          id={`instance-desc-${instance.id}`}
                          className="text-sm text-gray-600 mb-2"
                        >
                          {instance.description}
                        </p>
                        <hr className="my-3 border-gray-200" />
                        <div className="mb-2">
                          <div className="text-sm font-semibold text-gray-700">
                            Last Access
                          </div>
                          <div className="text-xs text-gray-500">
                            <Clock className="inline-block mr-1 w-3 h-3" />
                            {instance.lastAccess
                              ? formatDate(instance.lastAccess)
                              : "Just Now"}
                          </div>
                        </div>
                      </div>
                      <div
                        className={`mt-4 w-full bg-brand-blue text-white font-semibold rounded-lg flex items-center justify-center gap-2 text-base py-2 ${!instance.isActive ? 'opacity-50' : ''}`}
                        aria-hidden="true"
                      >
                       <Rocket className="w-5 h-5 mr-1 inline" />
                        Launch
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* Request More Access Section - Only for SSO/Internal Users who don't have all PROD instances */}
          {(loginType.includes("Azure AD") || loginType.includes("SSO")) && hasMoreProdInstances && (
            <div className="mt-10 mb-8 border-2 border-dashed border-brand-blue rounded-xl p-6 bg-white flex flex-col items-center">
              <div className="flex items-center gap-2 mb-2 text-brand-blue font-medium">
                <span className="flex items-center justify-center h-10 w-10 bg-primary-100 text-primary-600 rounded-full p-1 mr-2">
                <Plus className="w-6 h-6" />
                </span>
              </div>
              <p className="text-gray-600 mb-3 text-center">
                Need access to additional instance for your projects?
              </p>
              <Button
                className="bg-brand-blue hover:bg-brand-darkBlue focus:bg-brand-darkBlue focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 text-white px-6 py-2 rounded font-semibold border-none shadow-none transition-colors"
                onClick={() => setIsRequestModalOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsRequestModalOpen(true);
                  }
                }}
                aria-label="Request access to additional instances"
                tabIndex={0}
              >
                Request Instance
              </Button>
            </div>
          )}

          {/* Quick Start Guide Section */}
          {/* <div className="border-2 border-gray-200 rounded-xl p-6 bg-white">
            <div className="flex items-center gap-2 mb-3 text-[#2E7FA1] font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6m9-9a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Quick Start Guide
            </div>
            <p className="text-gray-600 mb-4">Get started with your instance access</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center">
                <div className="bg-[#2E7FA1] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mb-2">1</div>
                <div className="font-semibold mb-1 text-gray-800">Access Your Instance</div>
                <div className="text-xs text-gray-500 text-center">Click "Go to Dev Instance" to open your development environment.</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-[#2E7FA1] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mb-2">2</div>
                <div className="font-semibold mb-1 text-gray-800">Create Content</div>
                <div className="text-xs text-gray-500 text-center">Start building interactive learning modules and courses.</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-[#2E7FA1] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mb-2">3</div>
                <div className="font-semibold mb-1 text-gray-800">Collaborate</div>
                <div className="text-xs text-gray-500 text-center">Invite team members and work together on projects.</div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
      <Footer />
      
      {/* Request Instance Access Modal */}
      <RequestInstanceAccess
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        userInfo={{
          user: user,
          email: email || user,
          loginType: loginType
        }}
        accessibleInstances={accessibleInstances}
        canAccessDev={canAccessDev}
      />
    </section>
  );
};

export default InstanceSwitch;
